import { BaseDevice } from '../../../runtime/BaseDevice'
import type { HidChannel } from '../../../channels/hid/HidChannel'
import { DeviceManifest } from '../../../types/manifest'
import { ReportEvents } from '../../../events/ReportEvents'
import { NoticeEvents } from '../../../events/NoticeEvents'
import { CH585MKeyboardLighting } from '../../../chips/wch-ch585m/feature-impls/keyboard/keyboard-lighting'
import { CH585MConfig } from '../../../chips/wch-ch585m/feature-impls/keyboard/config'
import { CH585MKeyboardKeys } from '../../../chips/wch-ch585m/feature-impls/keyboard/keyboard-keys'
import { CH585MTravel } from '../../../chips/wch-ch585m/feature-impls/keyboard/key-travel'
import { deviceCalibration, ClassicKeyTravel } from '../../../types/keyTravel'
import { ch585mDeviceCustom } from '../../../types/config'
import { CH585MFirmware } from '../../../chips/wch-ch585m/feature-impls/keyboard/firmware'
import { rowColToIndex } from '../../../chips/wch-ch585m/protocols/addressGenerators'
import { ReportBackfill } from '../../../cache/ReportBackfill'
import type { ReportBackfillRule } from '../../../cache/types'
import { CH585M_REPORT_RULES } from '../../../chips/wch-ch585m/protocols/report-rules'
import { toAbsoluteConfigIndex } from '../../../utils/configSlots'

/** CH585M 系列设备连接协议值常量 */
const CONN_WIRED = 0xff as const
const CONN_WIRELESS = 0xa5 as const

/**
 * CH585M 系列设备驱动实现
 *
 * 这是一个 "容器类"，负责组装各个功能模块
 */
export class CH585MDevice extends BaseDevice {
  // 当前CH585M系列驱动sdk版本号
  public readonly sdkVersion = '0.0.1'

  /** Phase 4: 上报数据回填引擎实例,用于 close/disconnect 时调用 stop() */
  private readonly reportBackfill: ReportBackfill

  constructor(channel: HidChannel, manifest: DeviceManifest) {
    super(channel, manifest)

    // 自动检测连接类型
    this.detectConnectionType()

    // 组装具体的功能实现
    // 将 Feature 实例直接挂载到 features 容器中
    this.features.keyboardLighting = new CH585MKeyboardLighting(this)
    this.features.config = new CH585MConfig(this)
    this.features.keyboardKeys = new CH585MKeyboardKeys(this)
    this.features.keyTravel = new CH585MTravel(this)
    this.features.firmware = new CH585MFirmware(this)

    // Phase 4: 启动上报回填,让高频字段(亮度/速度/cfgSel/电量)通过 report 自动进缓存
    // 规则按 deviceUserCustom 强类型编写,注册时收敛为引擎的 unknown payload(运行时即该类型)
    this.reportBackfill = new ReportBackfill(this, CH585M_REPORT_RULES as ReportBackfillRule[])
    this.reportBackfill.start()
  }

  /**
   * 检测连接类型 (有线/无线)
   * 策略：
   * 1. 检查 Manifest 定义 (假设第二个 PID 为无线)
   * 2. 检查设备名称是否包含 "Receiver"
   */
  private detectConnectionType() {
    const info = this.channel.deviceInfo
    if (!info) return

    const pids = Array.isArray(this.manifest.hardware.productId)
      ? this.manifest.hardware.productId
      : [this.manifest.hardware.productId]

    let isWireless = false

    // 策略 1: 如果 PID 是列表中的第二个，认为是无线
    if (pids.length > 1 && info.productId === pids[1]) {
      isWireless = true
    }
    // 策略 2: 名称包含 "Receiver" 或 "Dongle"
    else if (info.productName && /Receiver|Dongle/i.test(info.productName)) {
      isWireless = true
    }

    if (isWireless) {
      this.connectionType = CONN_WIRELESS
      console.log(`[CH585MDevice] Detected Wireless Mode (PID: 0x${info.productId.toString(16)})`)
    } else {
      this.connectionType = CONN_WIRED
      console.log(`[CH585MDevice] Detected Wired Mode (PID: 0x${info.productId.toString(16)})`)
    }
  }

  protected onConnected(): void {
    console.log(`[CH585MDevice] Connected: ${this.manifest.model}`)
    // 初始化读取
    // 当前使用的配置
    this.features.config?.getCurrentConfig().then((config) => {
      this.currentConfig = config
    })
  }

  async checkConnect(): Promise<boolean> {
    try {
      const isConnected = this.channel?.isConnected
      if (!isConnected) {
        return false
      }
      await this.features.firmware?.getFirmwareVersion()
      return true
    } catch (_error) {
      return false
    }
  }

  protected onDisconnected(): void {
    this.reportBackfill.stop()
  }

  protected handleReport(_reportId: number, data: DataView): void {
    const buffer = new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    const type = buffer[0]
    this.dispatchEvent(type, buffer)
  }

  private async dispatchEvent(type: number, buffer: Uint8Array) {
    switch (type) {
      case 0x01:
        // 用户自定义数据
        const userBuffer = buffer.slice(1)
        // 解析用户自定义数据
        const userData = this.parseUserCustomData(userBuffer) as ch585mDeviceCustom
        // 如果上报了cfg配置变更，那么其他变更这次上报就不用发出消息提醒了
        let isReportCfg = false
        const configIndex = toAbsoluteConfigIndex(
          userData.cfgSel,
          this.manifest.capabilities,
          this.configSlotRoles,
        )
        const reportUserData =
          configIndex === userData.cfgSel ? userData : { ...userData, cfgSlot: configIndex }
        this.emit(ReportEvents.KeyUserData, reportUserData)
        const appUserData =
          configIndex === userData.cfgSel ? userData : { ...reportUserData, cfgSel: configIndex }
        // 同步当前配置
        if (this.currentConfig.index !== configIndex) {
          this.emit(ReportEvents.CfgChanged, appUserData)
          const currentConfig = this.configList.find((config) => config.index === configIndex)
          if (currentConfig) {
            this.currentConfig = currentConfig
          } else {
            const config = this.features.config
            if (config) {
              const configs = await config.getAllConfigs()
              const matchedConfig = configs.find((cfg) => cfg.index === configIndex)
              if (matchedConfig) {
                this.currentConfig = matchedConfig
              }
            }
          }
          this.emit(NoticeEvents.CfgChanged, this.currentConfig)
          isReportCfg = true
        }
        // 电量或充电状态有变化时才抛出事件
        const prevData = this.userCustomData as typeof userData | undefined
        if (
          prevData?.batPercent !== userData.batPercent ||
          prevData?.batStatus !== userData.batStatus
        ) {
          this.emit(ReportEvents.Battery, {
            batPercent: userData.batPercent,
            batStatus: userData.batStatus,
          })
        }
        // 设备灯效/亮度/速度变更时抛出事件
        const blModeChanged = prevData?.blMode !== userData.blMode
        const blLevelChanged = prevData?.blLevel !== userData.blLevel
        const blSpeedChanged = prevData?.blSpeed !== userData.blSpeed

        // 硬件侧按 FN 灯效切换键会改变 blMode，业务层据此同步当前灯效
        if (blModeChanged) {
          this.emit(ReportEvents.EffectChanged, userData.blMode)
        }
        if (blLevelChanged) {
          this.emit(ReportEvents.BrightnessChanged, userData.blLevel)
          if (prevData !== undefined && !isReportCfg) {
            this.emit(NoticeEvents.BrightnessChanged, userData.blLevel)
          }
        }
        if (blSpeedChanged) {
          this.emit(ReportEvents.SpeedChanged, userData.blSpeed)
          if (prevData !== undefined && !isReportCfg) {
            this.emit(NoticeEvents.SpeedChanged, userData.blSpeed)
          }
        }
        // 更新用户自定义数据
        this.userCustomData = appUserData
        // 灯效/亮度/速度上报时失效当前配置下的灯光缓存。
        // 上报只带 3 个字段，凑不出完整的 8 字节配置块，所以走失效而非回填，
        // 让下次读取从硬件拿到自洽的整块数据。
        if (blModeChanged || blLevelChanged || blSpeedChanged) {
          this.cache.invalidateByTag(`cfg:${this.currentConfig.index}`)
        }
        break
      case 0x02:
        // 重点关注NormalisedDelta
        // 键盘校准上传状态数据包（NormalisedDelta校准成功后就不会变了）
        // 演示模式上传状态数据包（NormalisedDelta会持续变化）
        const rxCxBuffer = buffer.slice(1)
        // 解析校准数据
        const calibData = this.parseCalibrationData(rxCxBuffer) as deviceCalibration
        this.emit(ReportEvents.KeyCalibUpload, calibData)
        break
      case 0x03:
        // 键盘按键状态数据包
        const rtBuffer = buffer.slice(1)
        const rtSwitchData = this.parseRTSwitchData(rtBuffer)
        // 物理 RT 切换键改变了硬件侧的全局 RT 配置索引,但没有 sendWrite,
        // 地址区间失效无法触发,必须按 tag 失效 getGlobalRTConfig 的缓存(tag-mapping §1 场景B)。
        this.cache.invalidateByTag('travel:global')
        this.emit(ReportEvents.KeyRtSwitch, rtSwitchData)
        this.emit(NoticeEvents.RTChanged, rtSwitchData.rt)
        break
    }
  }

  /**
   * 解析0x01用户自定义数据（9字节）
   * @param buffer 9字节的用户数据原始数据
   * @returns 解析后的deviceUserCustom对象
   */
  private parseUserCustomData(buffer: Uint8Array): ch585mDeviceCustom {
    // -------------------------- 1. 解析P_CFG（BYTE2，buffer[0]） --------------------------
    const pCfgByte = buffer[0]
    // const conProtocol = (pCfgByte & 0x0f) as 0 | 1 | 2 // 低4位：连接协议
    // const cfgSel = (pCfgByte >> 4) & 0x0f // 高4位：配置选择
    const conProtocol = 0
    // 这里直接赋值，因为软件说conProtocol没用上废弃了
    const cfgSel = pCfgByte

    // -------------------------- 2. 解析背光相关（BYTE3~BYTE5，buffer[1]~buffer[3]） --------------------------
    const blMode = buffer[1] // BL_MODE
    // 注意了，这里文档里跟实际数据包顺序不一致，实际数据包是先速度后亮度的，故这里进行修改
    const blSpeed = buffer[2] // BL_SP 0~100%
    const blLevel = buffer[3] // BL_LV 0~100%

    // -------------------------- 3. 解析睡眠时间（BYTE6，buffer[4]） --------------------------
    const sleepTime = buffer[4] // SLP_T 2~120分钟

    // -------------------------- 4. 解析回报率（BYTE7，buffer[5]） --------------------------
    const rptByte = buffer[5]
    const usbRpt = (rptByte & 0x0f) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 // 低4位：USB回报率
    const g24Rpt = ((rptByte >> 4) & 0x0f) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 // 高4位：2.4G回报率

    // -------------------------- 5. 解析功能组合（BYTE9~BYTE10，buffer[6]~buffer[7]，小端序uint16） --------------------------
    // 小端序：低字节在前(buffer[6])，高字节在后(buffer[7])
    const funWm16 = (buffer[7] << 8) | buffer[6]

    // 逐位解析（ARM位域低位到高位分配）
    const winMac = (funWm16 & 0x0001) as 0 | 1 // bit0
    const fnLock = ((funWm16 >> 1) & 0x0001) as 0 | 1 // bit1
    const winLock = ((funWm16 >> 2) & 0x0001) as 0 | 1 // bit2
    const fullKeyLock = ((funWm16 >> 3) & 0x0001) as 0 | 1 // bit3
    const blOnOff = ((funWm16 >> 4) & 0x0001) as 0 | 1 // bit4
    const disOnOff = ((funWm16 >> 5) & 0x0001) as 0 | 1 // bit5
    const keepliveFlag = ((funWm16 >> 6) & 0x0001) as 0 | 1 // bit6
    const aitErr = ((funWm16 >> 7) & 0x0001) as 0 | 1 // bit7
    const loBat = ((funWm16 >> 8) & 0x0001) as 0 | 1 // bit8
    const pairFlag = ((funWm16 >> 9) & 0x0001) as 0 | 1 // bit9
    const rfQuality = ((funWm16 >> 10) & 0x0003) as 0 | 1 | 2 | 3 // bit10~11
    const scanType = ((funWm16 >> 12) & 0x0003) as 0 | 1 | 2 // bit12~13
    const batStatus = ((funWm16 >> 14) & 0x0003) as 0 | 1 | 2 | 3 // bit14~15

    // -------------------------- 6. 解析电池百分比（BYTE8，buffer[8]） --------------------------
    const batPercent = buffer[8] // BAT% 0~100%

    // -------------------------- 7. 组装返回对象 --------------------------
    return {
      conProtocol,
      cfgSel,
      blMode,
      blLevel,
      blSpeed,
      sleepTime,
      usbRpt,
      g24Rpt,
      winMac,
      fnLock,
      winLock,
      fullKeyLock,
      blOnOff,
      disOnOff,
      keepliveFlag,
      aitErr,
      loBat,
      pairFlag,
      rfQuality,
      scanType,
      batStatus,
      batPercent,
    }
  }
  /**
   * 解析单个按键的校准上传数据（RxCx_1 12字节）
   * @param buffer 12字节的RxCx_1原始数据
   * @returns 解析后的deviceCalibration对象
   */
  private parseCalibrationData(buffer: Uint8Array): deviceCalibration {
    // -------------------------- 1. 解析行列索引（RC_idx，第0字节） --------------------------
    const rcIdxByte = buffer[0]
    const row = rcIdxByte & 0x07 // 提取低3位（row:3bit）
    const col = (rcIdxByte >> 3) & 0x1f // 提取高5位（col:5bit）

    // -------------------------- 2. 解析状态位（status，第1字节） --------------------------
    const statusByte = buffer[1]
    const ATIErr = (statusByte & 0x01) as 0 | 1
    // ATIStatus/MaxDeltaStatus为2bit，协议仅0/1/2有效，3做兼容处理
    const ATIStatusRaw = (statusByte >> 1) & 0x03
    const ATIStatus = (ATIStatusRaw > 2 ? 2 : ATIStatusRaw) as 0 | 1 | 2
    const MaxDeltaStatusRaw = (statusByte >> 3) & 0x03
    const MaxDeltaStatus = (MaxDeltaStatusRaw > 2 ? 2 : MaxDeltaStatusRaw) as 0 | 1 | 2
    const ATIHavePress = ((statusByte >> 5) & 0x01) as 0 | 1
    const KeyDisErr = ((statusByte >> 6) & 0x01) as 0 | 1

    // -------------------------- 3. 解析校准寄存器数据 --------------------------
    // 单字节数据
    const NormalisedDelta = buffer[2]
    const Movement = buffer[3]

    // 双字节数据（小端序：低字节在前，高字节在后）
    const Counts = (buffer[5] << 8) | buffer[4]
    const LTA = (buffer[7] << 8) | buffer[6]
    const MulDivPar = (buffer[9] << 8) | buffer[8]
    const SampleMaxDelta = (buffer[11] << 8) | buffer[10]

    // -------------------------- 4. 组装返回对象 --------------------------
    return {
      row,
      col,
      index: rowColToIndex(row, col),
      ATIErr,
      ATIStatus,
      MaxDeltaStatus,
      ATIHavePress,
      KeyDisErr,
      NormalisedDelta,
      Movement,
      Counts,
      LTA,
      MulDivPar,
      SampleMaxDelta,
    }
  }
  /**
   * 解析0x03RT键切换上传状态数据包
   */
  private parseRTSwitchData(buffer: Uint8Array) {
    const rt = buffer[0] // RT索引

    // 读取后续8字节数据并转为 BigInt (小端序)
    let rawBits = 0n
    for (let i = 0; i < 8; i++) {
      rawBits |= BigInt(buffer[1 + i]) << BigInt(8 * i)
    }

    // 按位域解析
    const keyTravel: ClassicKeyTravel = {
      absoluteTrigger: Number(rawBits & 0x3ffn),
      rtTriggerSensitivity: Number((rawBits >> 10n) & 0x3ffn),
      rtResetSensitivity: Number((rawBits >> 20n) & 0x3ffn),
      rtTriggerEnable: Boolean((rawBits >> 30n) & 0x1n),
      absoluteTriggerEnable: Boolean((rawBits >> 31n) & 0x1n),
      bottomDeadZone: Number((rawBits >> 32n) & 0xffn),
    }
    return { rt, keyTravel }
  }
}
